import { take } from 'rxjs/operators';
import { UsersSelectors } from 'src/app/features/users/state/users.selector-types';
import { Router } from '@angular/router';
import { InvoicesActions } from 'src/app/features/invoices/state/invoices.action-types';
import { Store } from '@ngrx/store';
import { AppState } from './../../../../app.states';
import { InvoiceService } from 'src/app/features/invoices/services/invoice.service';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Component, OnInit, Input, Inject, Output } from '@angular/core';
import { Toaster } from 'ngx-toast-notifications';
import { showToast } from 'src/app/shared/util/toaster.util';
import * as _ from 'lodash';

/** V3's refusal of a person's edit of a risk sub-check whose answer comes from V5 (HTTP 409, body is this code). */
const CHECK_ANSWERED_IN_V5 = 'RISK.CHECK_ANSWERED_IN_V5';

@Component({
  selector: 'app-checklist-modal',
  templateUrl: './checklist-modal.component.html',
  styleUrls: ['./checklist-modal.component.scss'],
})
export class ChecklistModalComponent implements OnInit {
  componentData;
  title = '';
  profile;
  percentage = '';

  /** Where each card was and what status it had the last time the server accepted it (or when the modal opened). */
  private savedStatus = new Map<any, string>();
  private savedPlace = new Map<any, { column: any; index: number }>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private invoiceService: InvoiceService,
    private store: Store<AppState>,
    private router: Router,
    public dialog: MatDialog,
    private toaster: Toaster
  ) {}

  ngOnInit(): void {
    this.componentData = _.cloneDeep(this.data);

    this.componentData.checklist.forEach(column => {
      column.items.forEach(item => {
        item.draggableRoles = this.getDraggableRolesForItem(item);
        this.applyReadOnly(item);
      });
    });

    if (this.componentData?.invoice?.isConfirming) {
      if (this.componentData.checklistSupplier) {
        this.componentData.checklistSupplier.forEach(column => {
          column.items.forEach(item => {
            item.draggableRoles = this.getDraggableRolesForItem(item);
            this.applyReadOnly(item);
          });
        });
      }
    }
    this.addCommentTootlip(this.componentData);
    this.addCavaliTooltip(this.componentData);

    switch (this.data.type) {
      case 'documents':
        this.title = 'Checklist de documentos';
        break;
      case 'risk':
        this.title = 'Checklist de riesgos';
        break;
      case 'cavali':
        this.title = 'Checklist de CAVALI';
        break;

      default:
        break;
    }

    this.store
      .select(UsersSelectors.profile())
      .pipe(take(1))
      .subscribe(profile => {
        if (profile) {
          this.profile = profile;
        }
      });

    this.percentage = this.data.percentage;
    this.rememberSavedPlaces();
  }

  /**
   * A risk sub-check flagged `isReadOnly` (V5 answers it, with its reason in `comment`) cannot be moved by
   * anyone, whatever their role. The kanban reads `readOnly`; the flag from the server is `isReadOnly`.
   */
  private isReadOnlyRiskCheck(item: any): boolean {
    return this.data?.type === 'risk' && item?.isReadOnly === true;
  }

  private applyReadOnly(item: any): void {
    if (this.isReadOnlyRiskCheck(item)) {
      item.readOnly = true;
      item.draggableRoles = [''];
    }
  }

  getDraggableRolesForItem(item: any): string[] {
    if (this.isReadOnlyRiskCheck(item)) {
      return [''];
    }

    if (
      (item.type === 'contract' && item.subType === 'user') ||
      (item.type === 'literal copy' && item.subType === 'user') ||
      (item.type === 'dni copy' && item.subType === 'user') ||
      (item.type === 'literal copy' && item.subType === 'provider') ||
      (item.type === 'dni copy' && item.subType === 'provider') ||
      (item.type === 'agreement' && item.subType === 'provider') ||
      (item.type === 'contract' && item.subType === 'provider')
    ) {
      return ['admin', 'super admin'];
    }

    if (item.type === 'email response' || item.type === 'payer response validation' || item.type === 'date amount') {
      return ['admin', 'super admin'];
    }

    if (item.type === 'compliance' && item.isReadOnly === false) {
      return ['admin', 'super admin'];
    }

    if (item.type === 'reliableNotification' && item.isReadOnly === false) {
      return ['admin', 'super admin'];
    }

    if (
      item.type === 'business relationship' ||
      item.type === 'invoice detail' ||
      item.type === 'coactive debt' ||
      item.type === 'expired finsmart' ||
      item.type === 'expired user' ||
      item.type === 'expired global' ||
      item.type === 'accepted risk'
    ) {
      return ['risk analyst'];
    }

    return [''];
  }

  getPercentageFromChecklist(): string {
    const allItems = this.componentData.checklist.flatMap(col => col.items);

    const approvedItems = allItems.filter(item => item.status === 'approved' || item.status === 'exception');

    return `${Math.floor((approvedItems.length / allItems.length) * 100)}%`;
  }

  drop(event) {
    const { card } = event.data;
    const isComplianceOrNotification = card.type === 'compliance' || card.type === 'reliableNotification';

    if (isComplianceOrNotification && event.confirmed) {
      this.invoiceService
        .updateCheck(this.data.invoice._id, card.status, this.data.type, card.type, undefined, event.comment || '')
        .subscribe(res => {
          this.updateComment(this.componentData, card);
        });

      this.percentage = `${Math.floor(
        (this.componentData.subChecks.filter(s => s.status === 'approved' || s.status === 'exception').length /
          this.componentData.subChecks.length) *
          100
      )}%`;

      return;
    }

    if (isComplianceOrNotification && !event.confirmed) {
      return;
    }
    if (this.data.type === 'documents') {
      this.invoiceService
        .updateCheck(this.data.invoice._id, card.status, this.data.type, card.type, card.subType, event.comment || '')
        .subscribe(res => {
          const result = Object(res);
          this.percentage = result.checklist.find(ch => ch.type === 'documents')?.percentage || '0%';

          this.updateComment(this.componentData, card);
        });
    } else {
      this.invoiceService
        .updateCheck(
          this.data.invoice._id,
          card.status,
          this.data.type,
          card.type,
          undefined,
          event?.data?.comment || ''
        )
        .subscribe(
          res => {
            this.updateComment(this.componentData, card);
            this.rememberSavedPlace(card);
          },
          this.data.type === 'risk' ? error => this.revertRefusedMove(card, error) : undefined
        );
      this.percentage = `${Math.floor(
        (this.componentData.subChecks.filter(s => s.status === 'approved' || s.status === 'exception').length /
          this.componentData.subChecks.length) *
          100
      )}%`;
    }
  }

  private allColumns(): any[] {
    return [...(this.componentData.checklist || []), ...(this.componentData.checklistSupplier || [])];
  }

  private rememberSavedPlaces(): void {
    this.allColumns().forEach(column =>
      column.items.forEach((item, index) => {
        this.savedStatus.set(item, item.status);
        this.savedPlace.set(item, { column, index });
      })
    );
  }

  private rememberSavedPlace(card: any): void {
    const column = this.allColumns().find(col => col.items.includes(card));
    if (column) {
      this.savedStatus.set(card, card.status);
      this.savedPlace.set(card, { column, index: column.items.indexOf(card) });
    }
  }

  /** The text the analyst reads when a save fails: the server's own message, or V5's for a refused risk edit. */
  saveErrorMessage(error: any): string {
    const body = error?.error;
    if (body === CHECK_ANSWERED_IN_V5) {
      return 'Este control lo responde Riesgos en V5';
    }
    if (error?.status >= 500 || error?.status === 0) {
      return 'Hubo un error, intentarlo más tarde';
    }
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (typeof body?.message === 'string' && body.message.trim()) {
      return body.message;
    }
    return 'Hubo un error, intentarlo más tarde';
  }

  /** The server refused the move: put the card back where it was, with its old status, and say why. */
  revertRefusedMove(card: any, error: any): void {
    const saved = this.savedPlace.get(card);
    if (saved) {
      this.allColumns().forEach(column => {
        const index = column.items.indexOf(card);
        if (index !== -1) {
          column.items.splice(index, 1);
        }
      });
      saved.column.items.splice(Math.min(saved.index, saved.column.items.length), 0, card);
      card.status = this.savedStatus.get(card);
    }

    this.percentage = `${Math.floor(
      (this.componentData.subChecks.filter(s => s.status === 'approved' || s.status === 'exception').length /
        this.componentData.subChecks.length) *
        100
    )}%`;

    showToast(this.toaster, this.saveErrorMessage(error), false, true);
  }

  goToDetail() {
    if (this.profile.role == 'admin') {
      if (this.data?.type === 'documents') {
        window.open(
          'https://lookerstudio.google.com/u/0/reporting/e87b7a98-c043-4c3a-9f7b-a0ce54ed8a19/page/bwSED',
          '_blank'
        );
      } else {
        this.router.navigate([]).then(result => {
          window.open('/invoices/' + this.data?.invoice?._id, '_blank');
        });
      }
    } else {
      this.router.navigate([]).then(result => {
        window.open('/risk-debtors/' + this.data?.invoice?.debtor?._id, '_blank');
      });
    }
  }

  statusEnabled() {
    return (
      this.data.invoice.status !== 'in process' &&
      this.data.invoice.status !== 'approved' &&
      this.data.invoice.status !== 'configured' &&
      this.data.invoice.status !== 'pending confirmation'
    );
  }

  statusEnabledRisk() {
    return (
      this.data.invoice.status !== 'in process' &&
      this.data.invoice.status !== 'approved' &&
      this.data.invoice.status !== 'configured' &&
      this.data.invoice.status !== 'pending confirmation' &&
      this.data.invoice.status !== 'for sale' &&
      this.data.invoice.status !== 'sale closed' &&
      this.data.invoice.status !== 'partially sold'
    );
  }

  addCommentTootlip(entity) {
    for (let index = 0; index < entity.subChecks.length; index++) {
      if (entity.subChecks[index].comment) {
        if (entity.subChecks[index].comment === 'taken by fund') {
          entity.subChecks[index].tooltipText = 'Lo tomará un fondo';
        } else if (entity.subChecks[index].comment === 'finsmart risk') {
          entity.subChecks[index].tooltipText = 'Riesgo finsmart';
        } else if (entity.subChecks[index].comment === 'on time') {
          entity.subChecks[index].tooltipText = 'Puntual';
        } else if (entity.subChecks[index].comment === 'early renewal') {
          entity.subChecks[index].tooltipText = 'Renovación anticipada';
        } else if (entity.subChecks[index].comment === 'reduction plan') {
          entity.subChecks[index].tooltipText = 'Plan de reducción';
        } else if (entity.subChecks[index].comment === 'overdraft') {
          entity.subChecks[index].tooltipText = 'Sobregiro';
        } else if (entity.subChecks[index].comment === 'pending invoice completion') {
          entity.subChecks[index].tooltipText = 'Factura pendiente de finalización';
        } else if (entity.subChecks[index].comment === 'limit decision') {
          entity.subChecks[index].tooltipText = 'Limit Decision';
        } else if (entity.subChecks[index].comment === 'reenganche') {
          entity.subChecks[index].tooltipText = 'Reenganche';
        } else if (entity.subChecks[index].comment === 'increase limit') {
          entity.subChecks[index].tooltipText = 'Increase limit';
        } else if (entity.subChecks[index].comment === 'fund') {
          entity.subChecks[index].tooltipText = 'Fondo';
        } else if (entity.subChecks[index].comment === 'extra limit 10') {
          entity.subChecks[index].tooltipText = '10% extra limit';
        } else if (entity.subChecks[index].comment === 'retail') {
          entity.subChecks[index].tooltipText = 'Retail';
        } else {
          entity.subChecks[index].tooltipText = entity.subChecks[index].comment;
        }
      }
    }
  }

  updateComment(entity, card) {
    for (let index = 0; index < entity.subChecks.length; index++) {
      if (entity.subChecks[index].type === card.type) {
        entity.subChecks[index].tooltipText = card.comment || '';
      }
    }
  }

  addCavaliTooltip(entity) {
    if (entity.type === 'cavali') {
      for (let index = 0; index < entity.subChecks.length; index++) {
        if (entity.subChecks[index].type == 'compliance') {
          entity.subChecks[index].tooltipText = entity.invoice.physicalInvoices
            .map(p => {
              let status = '---';

              if (p.cavaliStatus == 'in progress') {
                status = 'Sin respuesta';
              }

              if (p.cavaliPayerResponse) {
                switch (p.cavaliPayerResponse) {
                  case 'express compliance':
                    status = 'Conformidad Expresa';
                    break;
                  case 'rectified compliance':
                    status = 'Subsanada';
                    break;
                  case 'disagreement':
                  case 'rectified disagreement':
                    status = 'Disconformidad';
                    break;
                  default:
                    status = 'Conformidad presunta';
                    break;
                }
              }

              if (status == '---' && p.cavaliStatus) {
                status = p.cavaliStatus.charAt(0).toUpperCase() + p.cavaliStatus.slice(1);
              }

              return `• ${p.code} \xa0\ \xa0\ (${status})`;
            })
            .join('\n');
        }
      }
    }
  }
}
